import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Minimize2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface WhatsAppWidgetProps {
  isOpen?: boolean;
  phoneNumber?: string;
  welcomeMessage?: string;
  buttonColor?: string;
  position?: "right" | "left";
}

const WhatsAppWidget = ({
  isOpen = false,
  phoneNumber = "+971526385184",
  welcomeMessage = "Hello! I'm interested in your business services.",
  buttonColor = "#25D366",
  position = "right",
}: WhatsAppWidgetProps) => {
  const [isWidgetOpen, setIsWidgetOpen] = useState(isOpen);
  const [isMinimized, setIsMinimized] = useState(false);

  const toggleWidget = () => {
    setIsWidgetOpen(!isWidgetOpen);
    if (!isWidgetOpen) {
      setIsMinimized(false);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleStartChat = () => {
    const encodedMessage = encodeURIComponent(welcomeMessage);
    window.open(
      `https://wa.me/${phoneNumber.replace(/[^0-9]/g, "")}?text=${encodedMessage}`,
      "_blank",
    );
    setIsWidgetOpen(false);
  };

  return (
    <div
      className={`fixed bottom-4 ${position === "right" ? "right-4" : "left-4"} z-50`}
    >
      {/* WhatsApp button */}
      {!isWidgetOpen && (
        <Button
          onClick={toggleWidget}
          className="rounded-full h-14 w-14 shadow-lg text-white"
          style={{ backgroundColor: buttonColor }}
        >
          <MessageSquare size={24} />
        </Button>
      )}

      {/* WhatsApp widget */}
      <AnimatePresence>
        {isWidgetOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              height: isMinimized ? "auto" : 250,
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-[300px] rounded-lg shadow-xl overflow-hidden flex flex-col bg-white border border-gray-200"
          >
            {/* Header */}
            <div
              className="p-4 flex items-center justify-between"
              style={{ backgroundColor: buttonColor }}
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
                  <svg
                    viewBox="0 0 32 32"
                    className="h-5 w-5"
                    style={{ fill: buttonColor }}
                  >
                    <path d="M16.004 0h-0.008c-8.837 0-16 7.163-16 16 0 3.497 1.128 6.741 3.050 9.377l-1.993 5.949 6.136-1.955c2.527 1.682 5.536 2.657 8.766 2.657 8.837 0 16-7.163 16-16s-7.163-16-16-16zM25.166 22.566c-0.623 1.757-3.096 3.218-5.068 3.642-1.351 0.29-3.121 0.52-9.079-1.946-7.633-3.159-12.548-10.909-12.93-11.415-0.366-0.505-3.089-4.109-3.089-7.837 0-3.729 1.924-5.549 2.67-6.317 0.623-0.645 1.658-0.942 2.648-0.942 0.32 0 0.607 0.016 0.867 0.030 0.76 0.032 1.144 0.078 1.646 1.278 0.623 1.515 2.144 5.244 2.327 5.626 0.183 0.382 0.366 0.902 0.11 1.407-0.256 0.527-0.476 0.76-0.859 1.205-0.382 0.445-0.745 0.786-1.128 1.263-0.35 0.413-0.745 0.855-0.305 1.621 0.44 0.748 1.958 3.231 4.183 5.222 2.872 2.563 5.233 3.374 6.028 3.725 0.55 0.244 1.21 0.183 1.608-0.252 0.509-0.55 1.14-1.461 1.782-2.354 0.454-0.641 1.027-0.722 1.628-0.55 0.617 0.158 3.884 1.828 4.555 2.163 0.671 0.336 1.114 0.496 1.275 0.778 0.16 0.282 0.16 1.614-0.463 3.172z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-white">
                    WhatsApp Chat
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full text-white hover:bg-white/20"
                  onClick={toggleMinimize}
                >
                  {isMinimized ? (
                    <Maximize2 size={14} />
                  ) : (
                    <Minimize2 size={14} />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full text-white hover:bg-white/20"
                  onClick={toggleWidget}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>

            {/* Content */}
            {!isMinimized && (
              <div className="flex-1 p-4 flex flex-col justify-between">
                <div className="mb-4">
                  <p className="text-sm text-gray-700">
                    Chat with our business consultants directly on WhatsApp for
                    immediate assistance with your UAE business setup needs.
                  </p>
                </div>
                <Button
                  onClick={handleStartChat}
                  className="w-full text-white"
                  style={{ backgroundColor: buttonColor }}
                >
                  Start WhatsApp Chat
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppWidget;
